import { computed } from 'vue';
import { defineStore } from 'pinia';
import { useI18n } from 'vue-i18n';

import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import {
  isOwnStayIngredient,
  isOwnTransportIngredient,
} from '@apps/main/modules/recipe/ingredients/typeGuards';
import {
  useItineraryStore,
  useStatsigEventsStore,
  useGlobalCurrencyStore,
} from '@apps/main/new-desktop/core/stores';

import type { Money } from '@libs/trip-planner-interfaces';
import { AvailabilityOrder } from '@libs/trip-planner-interfaces';
import { isDefined } from '@libs/utils';
import { isFeatureToggleActive } from '@libs/feature-toggle';

import type { GlobalSuggestionSwitchOption } from '../components/globalSuggestionType';
import { GlobalSuggestionSwitchType } from '../components/globalSuggestionType';

export const useGlobalSuggestionSwitchStore = defineStore(
  'GlobalSuggestionSwitchStore',
  () => {
    const recipeStore = useRecipeStore();
    const itineraryStore = useItineraryStore();
    const statsigEventsStore = useStatsigEventsStore();
    const globalCurrencyStore = useGlobalCurrencyStore();

    const { t } = useI18n();

    // State
    const currentSwitchType = computed<GlobalSuggestionSwitchType | undefined>(
      () => {
        const globalAvailabilityOrder = recipeStore.recipe
          .map((ingredient) => {
            if (
              !isOwnStayIngredient(ingredient) &&
              !isOwnTransportIngredient(ingredient)
            ) {
              return ingredient.availabilityOrder;
            }

            return undefined;
          })
          .filter((availOrder) => availOrder !== undefined)
          .reduce(
            (
              acc: AvailabilityOrder | undefined,
              availOrder: AvailabilityOrder | undefined,
              index: number
            ): AvailabilityOrder | undefined => {
              if (index === 0) {
                acc = availOrder;
                return acc;
              }

              if (!acc || availOrder !== acc) {
                acc = undefined;
              }

              return acc;
            },
            undefined
          );

        return getGlobalSuggestionSwitchType(globalAvailabilityOrder);
      }
    );
    const suggestionOptions = computed<
      [GlobalSuggestionSwitchOption, GlobalSuggestionSwitchOption]
    >(() => [
      {
        type: GlobalSuggestionSwitchType.SemboRecommends,
        text: t('planner.recommended'),
        price: mostPopularPrice.value
          ? globalCurrencyStore.formatMoney(
              mostPopularPrice.value.amount,
              mostPopularPrice.value.currency
            )
          : undefined,
        isSelected:
          currentSwitchType.value ===
          GlobalSuggestionSwitchType.SemboRecommends,
      },
      {
        type: GlobalSuggestionSwitchType.LowestPrice,
        text: t('planner.lowest_price'),
        price: cheapestPrice.value
          ? globalCurrencyStore.formatMoney(
              cheapestPrice.value.amount,
              cheapestPrice.value.currency
            )
          : undefined,
        isSelected:
          currentSwitchType.value === GlobalSuggestionSwitchType.LowestPrice,
      },
    ]);

    const mostPopularPrice = computed<Money | null>(() =>
      isDefined(itineraryStore.userSelectedItinerary)
        ? itineraryStore.userSelectedItinerary.mostPopularPrice
        : itineraryStore.mostPopularItinerarySuggestionPrice
    );

    const cheapestPrice = computed<Money | null>(() =>
      isDefined(itineraryStore.userSelectedItinerary)
        ? itineraryStore.userSelectedItinerary.cheapestPrice
        : itineraryStore.cheapestItinerarySuggestionPrice
    );

    const isLoadingPrices = computed<boolean>(
      () =>
        itineraryStore.isSearchingGlobalSuggestionItineraries ||
        isFeatureToggleActive('loader')
    );

    // Actions

    function updateSwitchType(
      globalSuggestionSwitchType: GlobalSuggestionSwitchType
    ): void {
      statsigEventsStore.logTripOverviewGlobalSuggestionSwitched(
        globalSuggestionSwitchType ===
          GlobalSuggestionSwitchType.SemboRecommends
          ? 'popular'
          : 'cheapest'
      );
      recipeStore.recipe.forEach((ingredient) => {
        if (
          !isOwnStayIngredient(ingredient) &&
          !isOwnTransportIngredient(ingredient)
        ) {
          ingredient.availabilityOrder = getAvailabilityOrder(
            globalSuggestionSwitchType
          );
        }
      });
    }

    // Helper functions

    function getGlobalSuggestionSwitchType(
      availabilityOrder: AvailabilityOrder | undefined
    ): GlobalSuggestionSwitchType | undefined {
      if (availabilityOrder === AvailabilityOrder.Popular) {
        return GlobalSuggestionSwitchType.SemboRecommends;
      }

      if (availabilityOrder === AvailabilityOrder.Cheapest) {
        return GlobalSuggestionSwitchType.LowestPrice;
      }

      return undefined;
    }

    function getAvailabilityOrder(
      globalSuggestionSwitchType: GlobalSuggestionSwitchType
    ): AvailabilityOrder {
      if (
        globalSuggestionSwitchType ===
        GlobalSuggestionSwitchType.SemboRecommends
      ) {
        return AvailabilityOrder.Popular;
      }

      return AvailabilityOrder.Cheapest;
    }

    return {
      // State
      currentSwitchType,
      suggestionOptions,
      isLoadingPrices,

      // Actions
      updateSwitchType,
    };
  }
);
